import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Form, Row } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';
import { appConfig } from '../../../settings';
import { AppSystem, IEditForm, IFormFields, NotificationType } from '../../../interfaces';
import {
    useAppSystem,
    useCompanyConfig,
    useCompanyInfo,
    useCompanyIsMNOHost,
    useCompanyOperatorListIds,
    useCompanySalesRateList,
    useIsMyCompany,
    useProfile,
    useProfileIsRight,
    useRouterStore,
} from '../../../hooks';
import {
    addNotification,
    apiMessagesDeleteAllMessageBylListId,
    getProfileInfo,
    removeCompanyRateById,
    saveCompany,
    setCompanyForm,
    setCompanyInfoBillingEmailsStore,
} from '../../../middleware';
import { debounce, onFormFieldsChange } from '../../../instruments';
import { tI, tP } from '../../../translate';
import { Catcher, ShowComponentTitle } from '../../../Components';
import {
    EditCompanyBillingInfo,
    EditCompanyContactInfo,
    EditCompanyCustomDocs,
    EditCompanyCustomize,
    EditCompanyCustomizeAbout,
    EditCompanyFieldsMrcNrc,
    EditCompanyFieldsNrc,
    EditCompanyFinancial,
    EditCompanyInfo,
    EditCompanyInvoiceForm,
    EditCompanyLogo,
    EditCompanySalesRateList,
    EditCompanySubscriberVisibilityGranted,
} from './../Components';
import Styles from '../../styles/m_itemCardStyles.less';

export const EditCompanyForm: React.FC<IEditForm> = observer(({ setEditMode, isNew = false }: IEditForm) => {
    const [isSending, setSending] = useState(false);
    const [isRateList, setIsRateList] = useState(false);
    const [editingIsStarted, setEditingIsStarted] = useState(false);
    const [form] = Form.useForm();
    const companyInfo = useCompanyInfo();
    const { mfa } = useCompanyConfig();
    const { type: cdrSquashIntervalType, value: cdrSquashIntervalValue } = appConfig.defaultInterval;
    const { id, customerPrivacy, whiteLabel, parentId, customAboutEnabled, slug } = companyInfo;
    const { whiteLabel: myWhiteLabel, companyId: profileCompanyId, isMainMaternalCompany } = useProfile();
    const isChild = parentId === profileCompanyId;
    const salesRateList = useCompanySalesRateList();
    const idsSalesRateList = salesRateList.map((c) => c.id);
    const operatorListIds = useCompanyOperatorListIds();
    const initialValues = {
        cdrSquashIntervalValue,
        cdrSquashIntervalType,
        mfa,
        ...companyInfo,
        salesRateList,
        operatorList: operatorListIds,
    };

    const isMyCompany = useIsMyCompany();
    const routerStore = useRouterStore();
    const { companyId } = useProfile();
    const { id: currentCompanyId } = companyInfo;
    const isLord = useProfileIsRight('LORD');
    const isMNOHost = useCompanyIsMNOHost();
    const appSystem = useAppSystem();

    const checkValid = (): void => {
        form.validateFields();
    };

    useEffect(() => {
        if (customerPrivacy && !isLord && !isMyCompany && !editingIsStarted) {
            addNotification({
                type: NotificationType.WARNING,
                message: 'Not authorized!',
                description: `Your company has not been granted permission to view this company information`,
                duration: 5,
            });

            routerStore.goTo('Company', { params: { id } });
        }
    }, [customerPrivacy]);

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [JSON.stringify(initialValues)]);

    const onFieldsChange = (changedFields: FieldData[]): void => {
        setEditingIsStarted(true);
        onFormFieldsChange({
            changedFields,
            initialValues,
            listIds: {
                salesRateList: idsSalesRateList,
            },
            setForm: setCompanyForm,
        });
    };

    const deleteCompanyRateById = (id: string): void => {
        apiMessagesDeleteAllMessageBylListId(id);
        removeCompanyRateById(id);
    };

    const updateCompany = async (): Promise<void> => {
        setSending(true);
        const isError = await saveCompany(isNew);
        if (isError) {
            checkValid();
        } else {
            setEditMode && setEditMode(false);
        }

        if (isMyCompany) {
            await getProfileInfo(true);
        }

        setSending(false);
    };

    const onValuesChange = (fields: IFormFields): void => {
        const name = Object.keys(fields)[0];

        if (name === 'salesRateList') {
            setIsRateList(true);
        } else {
            setIsRateList(false);
            if (name === 'billingEmails') {
                const changesList = fields[name];
                if (Array.isArray(changesList)) {
                    const billingEmailsList = form.getFieldValue('billingEmails');
                    setCompanyInfoBillingEmailsStore(billingEmailsList);
                }
            }

            form.validateFields([name]).then();
        }
    };

    return (
        <Catcher>
            <div className={Styles.main}>
                <ShowComponentTitle text={`${tI('Edit')} ${tP('Company')} #${id || ''}`} />
                <Form.Provider>
                    <Form
                        form={form}
                        name={'companyEdit'}
                        className={'formStyle'}
                        initialValues={initialValues}
                        layout={'vertical'}
                        onFieldsChange={
                            isRateList ? onFieldsChange : debounce(onFieldsChange, appConfig.delayFormUpdateState)
                        }
                        onValuesChange={onValuesChange}
                        scrollToFirstError={true}
                        validateTrigger={'onBlur'}
                    >
                        <Row>
                            <EditCompanyInfo
                                isMyCompany={isMyCompany}
                                isNew={isNew}
                                isSending={isSending}
                                save={updateCompany}
                                setEditMode={setEditMode}
                            />
                            <EditCompanyContactInfo />
                            <EditCompanyBillingInfo form={form} />
                            {isNew || isLord || isMainMaternalCompany || isChild ? (
                                <EditCompanyFinancial
                                    checkValid={checkValid}
                                    form={form}
                                    initialValues={initialValues}
                                />
                            ) : null}
                            {(isMainMaternalCompany || companyId === currentCompanyId) && isMNOHost ? (
                                <EditCompanySubscriberVisibilityGranted />
                            ) : null}
                            {!isMNOHost ? <EditCompanySalesRateList deleteItem={deleteCompanyRateById} /> : null}
                            {(whiteLabel && myWhiteLabel) || isLord ? <EditCompanyLogo /> : null}
                            {isLord && !isNew ? <EditCompanyFieldsMrcNrc /> : null}
                            {isLord && !isNew ? <EditCompanyFieldsNrc /> : null}
                            {isLord && appSystem === AppSystem.BILLING ? (
                                <EditCompanyInvoiceForm
                                    companyId={currentCompanyId || ''}
                                    onFieldsChange={onFieldsChange}
                                />
                            ) : null}
                            {isLord && !isNew ? <EditCompanyCustomize /> : null}
                            {(isLord && !isNew && slug) || currentCompanyId === '2' ? <EditCompanyCustomDocs /> : null}
                        </Row>
                    </Form>
                    {isLord && !isNew && customAboutEnabled ? <EditCompanyCustomizeAbout /> : null}
                </Form.Provider>
            </div>
        </Catcher>
    );
});
